import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Button, Code, Group, Stack, Text, Tooltip, Skeleton as MSkeleton } from '@mantine/core'
import { IconAlertCircle, IconAlertTriangle, IconEye, IconLock, IconPlugConnectedX, IconStack2 } from '@tabler/icons-react'
import { DataTable, EmptyState, PageTitle, SectionCard, StatTile } from '@lighthouse/design'
import type { DataTableColumn } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { DeadLetterPeekDrawer } from '../components/queues/DeadLetterPeekDrawer'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { useDeadLetterStats } from '../hooks/useQueues'
import { LinkedFromSignal, LinkedMark } from '../components/signals/LinkedFromSignal'
import { isLinked, linkedKey } from '../utils/deepLink'
import type { AdminGate } from '../utils/adminGate'
import { ADMIN_ROLE_REQUIRED, NOT_MEASURED, errorKind, errorSummary, exactNumber, whenLabel } from '../utils/format'
import type { DeadLetterQueueStats } from '../types/queues'

const SUBTITLE = 'How deep each dead-letter queue is, and what is sitting in it'
const RUNBOOK = 'docs/operations/dead-letter-runbook.md'

/**
 * Queues ("Holds") — the dead-letter queues, how deep each one is, and a peek at
 * what is in it.
 *
 * `GET api/system/dead-letters` served depths and a message peek with no client
 * at all. This page is those two endpoints and nothing more: no replay, no purge,
 * no requeue.
 *
 * Five decisions shape it:
 *
 *  1. **An unavailable queue is not an empty one.** When the monitor cannot
 *     inspect a queue the server still sends `messageCount: 0` as a placeholder,
 *     beside `unavailable: true`. Printed, that 0 is the most reassuring number
 *     on the page and it is not a measurement — so an unavailable depth is a dash
 *     with the word "unavailable", and it is left out of the total rather than
 *     added in as zero. A measured zero is printed plainly, never green.
 *  2. **The total says what it is over.** "Total depth over reachable queues
 *     only", beside how many were reachable, and a dash when none were — a sum
 *     over nothing is not zero.
 *  3. **The broker's reason is for administrators.** `error` is broker exception
 *     text or a RabbitMQ reply, and it can name hosts and entities. The endpoint
 *     serves it to any authenticated caller, the shared API key included, so the
 *     page shows it only on gate `admin` and says "unavailable" to everyone else.
 *  4. **The peek is admin-only, and the button says so before it is pressed.**
 *     It returns whole message bodies — a dead-lettered report export event
 *     carries a report name and the requesting user's id — so the server gates it
 *     on LighthouseAdmin. On gate `denied` the button is disabled with the
 *     server's reason; on `unknown` it is enabled and the drawer renders the
 *     endpoint's own 403 as restricted.
 *  5. **Failed and empty are different pages.** "No dead-letter queues are
 *     configured" is a finding about configuration; a failed request is a fault
 *     between this page and the API that says nothing about whether any queue is
 *     deep. They never share a sentence.
 *
 * The monitor is RabbitMQ in Development and Service Bus in Azure
 * (Lighthouse.Api Program.cs registers one or the other by environment). The
 * page names it only when the queue names themselves reveal it — see
 * `revealedBroker` — and otherwise says nothing, rather than guessing from the
 * SPA's own build.
 */
export function QueuesPage() {
  const gate = useLighthouseAdmin()
  const { data, isLoading, isError, error, dataUpdatedAt } = useDeadLetterStats()
  const [peeking, setPeeking] = useState<DeadLetterQueueStats | null>(null)
  // `?queue=`: the Signal Inbox's link to one dead-letter queue, marked in the table — or said to be missing.
  const [search] = useSearchParams()
  const linked = linkedKey(search, 'queue')

  const queues = Array.isArray(data) ? data : null
  const broker = queues ? revealedBroker(queues) : null

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={broker ? `${SUBTITLE}, as ${broker} reports it` : SUBTITLE}>
        Queues
      </PageTitle>

      {isError && !data ? (
        <StatsError error={error} />
      ) : isLoading || !data ? (
        <Stack gap="md">
          <MSkeleton height={120} />
          <MSkeleton height={240} />
        </Stack>
      ) : !queues ? (
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} />}
          title="The API did not answer with a list of queues"
          message="This says nothing about whether any dead-letter queue is deep."
        />
      ) : queues.length === 0 ? (
        <EmptyState
          icon={<IconStack2 size={28} stroke={1.5} />}
          title="No dead-letter queues are configured"
          message={
            'The monitor answered, and it has no consumer queue or subscription to inspect. That is a '
            + 'configuration finding (DeadLetter:RabbitMqConsumerQueues in Development, '
            + 'DeadLetter:ServiceBusSubscriptions in Azure), not a clean bill of health.'
          }
        />
      ) : (
        <>
          {isError && <RefreshFailed dataUpdatedAt={dataUpdatedAt} />}
          {linked && (
            <LinkedFromSignal what="dead-letter queue" id={linked} found={queues.some((q) => isLinked(q.queueName, linked))} />
          )}
          <DepthSummary queues={queues} />
          <QueueTable queues={queues} gate={gate} onPeek={setPeeking} linked={linked} />
        </>
      )}

      <Text size="sm" c="dimmed">
        There is no replay control on this page, by design. Replaying a dead-lettered message is a manual
        runbook step, and it starts with fixing whatever put the message there: see <Code>{RUNBOOK}</Code>.
      </Text>

      <DeadLetterPeekDrawer queue={peeking} onClose={() => setPeeking(null)} />
    </Stack>
  )
}

/**
 * Which broker the stats reveal, or null when they do not.
 *
 * Read off the queue names the two monitors build, and nothing else: Service Bus
 * names a subscription's dead-letter sub-queue `<topic>/<subscription>/$DeadLetterQueue`,
 * and RabbitMQ names a DLQ as its consumer queue plus a suffix (`.dlq` by
 * default). Every row must agree; an empty, mixed or unrecognised list names
 * nothing.
 */
function revealedBroker(queues: DeadLetterQueueStats[]): string | null {
  if (queues.length === 0) return null
  if (queues.every((q) => q.queueName.endsWith(`/${q.sourceConsumer}/$DeadLetterQueue`))) {
    return 'Azure Service Bus'
  }
  if (queues.every((q) => q.queueName.length > q.sourceConsumer.length
    && q.queueName.startsWith(q.sourceConsumer)
    && !q.queueName.includes('/'))) {
    return 'RabbitMQ'
  }
  return null
}

/**
 * The stats request failed, so nothing is known about any queue.
 *
 * Every branch ends by saying so: a red panel on a queues page reads as "the
 * queues are in trouble", and all this knows is that the API did not answer.
 */
function StatsError({ error }: { error: unknown }) {
  const kind = errorKind(error)

  if (kind === 'forbidden') {
    return <RestrictedState surface="Dead-letter queue depths" />
  }

  const cause =
    kind === 'unauthorized'
      ? 'The API did not accept this session’s credentials. Sign in again, or check the API key.'
      : kind === 'network'
        ? 'The request never got an answer: the API may not be running, or the network refused it.'
        : errorSummary(error) ?? 'The request failed.'

  return (
    <EmptyState
      icon={kind === 'network' ? <IconPlugConnectedX size={28} stroke={1.5} /> : <IconAlertCircle size={28} stroke={1.5} />}
      title="Could not read dead-letter queues"
      message={`${cause} This says nothing about whether any are deep.`}
    />
  )
}

/** A background refresh failed; the table below is the last good answer, and says when it was. */
function RefreshFailed({ dataUpdatedAt }: { dataUpdatedAt: number }) {
  const when = dataUpdatedAt > 0 ? whenLabel(new Date(dataUpdatedAt).toISOString()) : NOT_MEASURED
  return (
    <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="The last refresh failed">
      <Text size="sm">These depths were read {when}, and nothing newer has come back since.</Text>
    </Alert>
  )
}

/**
 * How many queues answered, and the depth over those alone.
 *
 * An unavailable queue's placeholder 0 is never summed: the total is over
 * reachable queues only, and says so, and it is a dash when no queue answered.
 * No status role on a zero, and none on the reachable count — a queue nobody
 * could inspect is unknown, not bad, and colouring it would make it an answer.
 */
function DepthSummary({ queues }: { queues: DeadLetterQueueStats[] }) {
  const reachable = queues.filter((q) => !q.unavailable)
  const unavailable = queues.length - reachable.length
  const depth = reachable.reduce((sum, q) => sum + q.messageCount, 0)

  return (
    <SectionCard
      title="Dead-letter depth"
      subtitle={`${exactNumber(reachable.length)} of ${exactNumber(queues.length)} queues reachable`}
    >
      <div
        className="lh-overview-grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
      >
        <StatTile
          label="Queues reachable"
          value={`${exactNumber(reachable.length)} of ${exactNumber(queues.length)}`}
          sublabel={
            unavailable === 0
              ? 'every configured queue answered'
              : `${exactNumber(unavailable)} could not be inspected`
          }
        />
        <StatTile
          label="Total depth"
          value={reachable.length === 0 ? NOT_MEASURED : exactNumber(depth)}
          status={reachable.length > 0 && depth > 0 ? 'warning' : undefined}
          sublabel={
            reachable.length === 0
              ? 'no queue could be inspected'
              : 'over reachable queues only'
          }
        />
      </div>
    </SectionCard>
  )
}

function QueueTable({
  queues,
  gate,
  onPeek,
  linked = null,
}: {
  queues: DeadLetterQueueStats[]
  gate: AdminGate
  onPeek: (queue: DeadLetterQueueStats) => void
  /** A `?queue=` deep link's queue name, marked in its row. */
  linked?: string | null
}) {
  const showReasons = gate.state === 'admin'
  const anyUnavailable = queues.some((q) => q.unavailable)

  const columns: DataTableColumn<DeadLetterQueueStats>[] = [
    {
      key: 'queue',
      header: 'Dead-letter queue',
      render: (q) => (
        <Stack gap={2}>
          {isLinked(q.queueName, linked) && <LinkedMark />}
          <Code style={{ overflowWrap: 'anywhere' }}>{q.queueName}</Code>
          {q.unavailable && showReasons && q.error && (
            <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{firstLine(q.error)}</Text>
          )}
        </Stack>
      ),
    },
    {
      key: 'consumer',
      header: 'Consumer',
      render: (q) => <Text size="sm" ff="monospace" style={{ overflowWrap: 'anywhere' }}>{q.sourceConsumer}</Text>,
    },
    {
      key: 'messages',
      header: 'Messages',
      numeric: true,
      render: (q) => <DepthCell queue={q} />,
    },
    {
      key: 'peek',
      header: '',
      width: 110,
      render: (q) => <PeekButton queue={q} gate={gate} onPeek={onPeek} />,
    },
  ]

  return (
    <SectionCard title="Queues" subtitle="One row per consumer queue or subscription the monitor is configured with">
      <Stack gap="sm">
        {gate.state === 'denied' && (
          <Group gap={6} wrap="nowrap" align="flex-start">
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>Peeking at messages:</Text>
            <RestrictedState surface="The dead-letter peek" reason={gate.reason} compact />
          </Group>
        )}

        <DataTable
          columns={columns}
          rows={queues}
          rowKey={(q) => `${q.queueName}::${q.sourceConsumer}`}
        />

        {anyUnavailable && !showReasons && (
          <Text size="xs" c="dimmed">
            Why a queue could not be inspected is shown to Lighthouse.Admin holders only: the broker&rsquo;s
            reason can name hosts and entities.
          </Text>
        )}
      </Stack>
    </SectionCard>
  )
}

/** A measured depth, or a dash that says "unavailable" — never the server's placeholder 0. */
function DepthCell({ queue }: { queue: DeadLetterQueueStats }) {
  if (queue.unavailable) {
    return (
      <Group gap={6} wrap="nowrap" justify="flex-end">
        <Text span c="dimmed" aria-hidden="true">{NOT_MEASURED}</Text>
        <Text span size="sm" c="dimmed">unavailable</Text>
      </Group>
    )
  }
  return (
    <Text span size="sm" fw={queue.messageCount > 0 ? 600 : undefined}>
      {exactNumber(queue.messageCount)}
    </Text>
  )
}

/**
 * Disabled — not hidden — on `denied`, with the server's reason on hover and in
 * the note above the table: the row still reads as "a queue you could look into",
 * which is true, and the reason says what is missing. `data-disabled` rather
 * than `disabled`, so the tooltip still receives the pointer.
 */
function PeekButton({
  queue,
  gate,
  onPeek,
}: {
  queue: DeadLetterQueueStats
  gate: AdminGate
  onPeek: (queue: DeadLetterQueueStats) => void
}) {
  const denied = gate.state === 'denied'
  const reason = denied && gate.reason.trim() ? `${ADMIN_ROLE_REQUIRED}. ${gate.reason.trim()}` : ADMIN_ROLE_REQUIRED

  return (
    <Tooltip label={reason} disabled={!denied} multiline w={260} withArrow>
      <Button
        size="xs"
        variant="light"
        leftSection={denied ? <IconLock size={14} /> : <IconEye size={14} />}
        data-disabled={denied || undefined}
        aria-disabled={denied || undefined}
        onClick={(event) => {
          if (denied) {
            event.preventDefault()
            return
          }
          onPeek(queue)
        }}
      >
        Peek
      </Button>
    </Tooltip>
  )
}

/** The first line of broker text, capped the way errorSummary caps an error. */
function firstLine(text: string): string {
  const first = text.split(/\r?\n/, 1)[0].trim()
  return first.length > 200 ? `${first.slice(0, 200)}…` : first
}
